import {
  Activity,
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  BellRing,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  MapPinned,
  MessageSquareHeart,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import IssueCard from "../components/IssueCard";
import ResponseModal from "../components/ResponseModal";
import { candidates, getImageUrl, issues } from "../services/api";

const StatCard = ({ label, value, accent, note, icon: Icon }) => (
  <div className="relative overflow-hidden rounded-[1.75rem] border border-white/70 bg-white/90 p-5 shadow-lg shadow-slate-200/50 backdrop-blur">
    <div className={`absolute inset-x-0 top-0 h-1 ${accent}`} />
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
          {label}
        </p>
        <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{value}</p>
        <p className="mt-2 text-sm text-slate-500">{note}</p>
      </div>
      <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [assignedIssues, setAssignedIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [responseOpen, setResponseOpen] = useState(false);
  const [responseSubmitting, setResponseSubmitting] = useState(false);
  const [resolvingIssueId, setResolvingIssueId] = useState("");

  const loadDashboard = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [dashboardResponse, assignedResponse] = await Promise.all([
        candidates.getDashboard(),
        issues.getAssignedToMe(),
      ]);

      setDashboard(dashboardResponse.data);
      setAssignedIssues(assignedResponse.data?.issues || []);
      setError("");
    } catch (requestError) {
      console.error("Candidate dashboard load failed:", requestError);
      const nextError =
        requestError.response?.data?.message || "Failed to load candidate dashboard.";
      setError(nextError);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const trendData = useMemo(() => {
    const source = assignedIssues.slice(0, 6).reverse();
    return source.map((issue) => ({
      name: issue.title.length > 14 ? `${issue.title.slice(0, 14)}...` : issue.title,
      upvotes: issue.upvotesCount || 0,
      responses: Array.isArray(issue.updates) ? issue.updates.length : 0,
    }));
  }, [assignedIssues]);

  const handleRespond = (issue) => {
    setSelectedIssue(issue);
    setResponseOpen(true);
  };

  const submitResponse = async (issueId, message) => {
    try {
      setResponseSubmitting(true);
      const response = await issues.respond(issueId, { message });
      toast.success(response.data?.message || "Response submitted successfully.");
      setResponseOpen(false);
      setSelectedIssue(null);
      await loadDashboard({ silent: true });
    } catch (requestError) {
      console.error("Candidate response failed:", requestError);
      toast.error(
        requestError.response?.data?.message || "Failed to send candidate response.",
      );
    } finally {
      setResponseSubmitting(false);
    }
  };

  const handleResolve = async (issue) => {
    try {
      setResolvingIssueId(issue._id);
      const response = await issues.resolve(issue._id, {
        note: "Resolved and action has been initiated from the candidate dashboard.",
      });
      toast.success(response.data?.message || "Issue resolved successfully.");
      await loadDashboard({ silent: true });
    } catch (requestError) {
      console.error("Issue resolve failed:", requestError);
      toast.error(requestError.response?.data?.message || "Failed to resolve issue.");
    } finally {
      setResolvingIssueId("");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(255,153,51,0.16),_transparent_34%),linear-gradient(180deg,#fff8ef_0%,#ffffff_42%,#eef8ef_100%)]">
        <div className="flex items-center gap-3 rounded-full bg-white px-6 py-4 shadow-lg">
          <LoaderCircle className="h-5 w-5 animate-spin text-orange-500" />
          <span className="text-sm font-semibold text-slate-700">
            Loading your command center...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(255,153,51,0.16),_transparent_34%),linear-gradient(180deg,#fff8ef_0%,#ffffff_42%,#eef8ef_100%)] px-4">
        <div className="max-w-lg rounded-[2rem] border border-rose-100 bg-white p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-500" />
          <h1 className="mt-4 text-2xl font-black text-slate-900">Dashboard unavailable</h1>
          <p className="mt-3 text-sm text-slate-600">{error}</p>
          <button
            type="button"
            onClick={() => loadDashboard()}
            className="mt-6 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Retry Dashboard
          </button>
        </div>
      </div>
    );
  }

  const notifications = dashboard?.notifications || [];
  const recentIssues = dashboard?.recentIssues || assignedIssues.slice(0, 4);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,153,51,0.14),_transparent_34%),linear-gradient(180deg,#fff8ef_0%,#ffffff_42%,#eef8ef_100%)] px-4 py-10">
      <div className="mx-auto max-w-7xl space-y-8">
        <section className="relative overflow-hidden rounded-[2.25rem] border border-white/60 bg-[linear-gradient(135deg,rgba(15,23,42,0.96),rgba(30,41,59,0.88)_45%,rgba(13,148,136,0.82))] p-7 text-white shadow-2xl shadow-slate-900/20">
          <div className="absolute -right-16 top-0 h-52 w-52 rounded-full bg-orange-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-44 w-44 rounded-full bg-emerald-300/10 blur-3xl" />
          <div className="relative flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <img
                src={getImageUrl(dashboard?.image_url, "image/default-avatar.png")}
                alt={dashboard?.name || "Candidate"}
                className="h-24 w-24 rounded-[2rem] border border-white/20 object-cover shadow-lg"
              />
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white/80">
                  <ShieldCheck className="h-4 w-4" />
                  Candidate Command Center
                </div>
                <h1 className="mt-3 flex items-center gap-2 text-3xl font-black tracking-tight sm:text-4xl">
                  {dashboard?.name}
                  {dashboard?.verified && <BadgeCheck className="h-6 w-6 text-emerald-300" />}
                </h1>
                <p className="mt-2 text-sm text-white/75 sm:text-base">
                  {dashboard?.party || "Independent"} • {dashboard?.position || "Representative"} • {dashboard?.constituency || "Unknown constituency"}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-white/75">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                    <MapPinned className="h-4 w-4" />
                    {dashboard?.district || "District unavailable"}, {dashboard?.state || "State unavailable"}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                    <Sparkles className="h-4 w-4" />
                    Response rate {dashboard?.responseRate || 0}%
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => loadDashboard({ silent: true })}
                className="rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                {refreshing ? "Refreshing..." : "Refresh Live Data"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/issues")}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-orange-50"
              >
                View Public Issues
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Total Issues"
            value={dashboard?.stats?.totalIssues || 0}
            note="All civic issues currently routed to your desk."
            accent="bg-gradient-to-r from-orange-400 to-orange-500"
            icon={Activity}
          />
          <StatCard
            label="Resolved %"
            value={`${dashboard?.stats?.resolvedPercentage || 0}%`}
            note={`${dashboard?.stats?.resolvedIssues || 0} issues fully closed.`}
            accent="bg-gradient-to-r from-emerald-400 to-emerald-500"
            icon={CheckCircle2}
          />
          <StatCard
            label="Pending"
            value={dashboard?.stats?.pendingIssues || 0}
            note="Needs active response or field follow-up."
            accent="bg-gradient-to-r from-amber-400 to-amber-500"
            icon={Clock3}
          />
          <StatCard
            label="Rating"
            value={dashboard?.stats?.rating || 0}
            note="Community perception snapshot from platform activity."
            accent="bg-gradient-to-r from-sky-400 to-sky-500"
            icon={MessageSquareHeart}
          />
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.45fr_0.85fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Assigned Issues
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                  Your active civic queue
                </h2>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                {assignedIssues.length} assigned
              </span>
            </div>

            <div className="mt-6 space-y-4">
              {assignedIssues.length === 0 ? (
                <div className="rounded-[1.75rem] border border-dashed border-slate-200 bg-[linear-gradient(135deg,#fff8ef,#f8fff9)] p-10 text-center">
                  <BellRing className="mx-auto h-10 w-10 text-slate-300" />
                  <h3 className="mt-4 text-xl font-bold text-slate-900">
                    No issues assigned yet
                  </h3>
                  <p className="mt-2 text-sm text-slate-500">
                    Once citizens raise issues linked to your constituency, they will appear here for action.
                  </p>
                </div>
              ) : (
                assignedIssues.map((issue) => (
                  <IssueCard
                    key={issue._id}
                    issue={issue}
                    variant="candidate-dashboard"
                    onRespond={handleRespond}
                    onResolve={handleResolve}
                    onViewDetails={() => navigate(`/issues/${issue._id}`)}
                    isResolving={resolvingIssueId === issue._id}
                  />
                ))
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Notifications
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Live briefing
              </h2>

              <div className="mt-5 space-y-3">
                {notifications.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-500">
                    No fresh alerts right now.
                  </p>
                ) : (
                  notifications.map((notification, index) => (
                    <div
                      key={`${notification.issueId || "notice"}-${index}`}
                      className="rounded-[1.5rem] border border-slate-100 bg-slate-50 px-4 py-4"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-500">
                        {notification.type || "Update"}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        {notification.message}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Engagement Trend
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Constituency pulse
              </h2>

              <div className="mt-5 h-64">
                {trendData.length === 0 ? (
                  <div className="flex h-full items-center justify-center rounded-[1.5rem] bg-slate-50 text-sm text-slate-500">
                    Trend insights will appear as issues gather support.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="upvotes" stroke="#f97316" strokeWidth={3} dot={false} />
                      <Line type="monotone" dataKey="responses" stroke="#10b981" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Recent Issues
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Fast access
              </h2>

              <div className="mt-5 space-y-3">
                {recentIssues.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-500">
                    Nothing recent yet.
                  </p>
                ) : (
                  recentIssues.map((issue) => (
                    <button
                      key={issue._id}
                      type="button"
                      onClick={() => navigate(`/issues/${issue._id}`)}
                      className="flex w-full items-center justify-between rounded-[1.35rem] border border-slate-100 bg-slate-50 px-4 py-4 text-left transition hover:border-orange-200 hover:bg-orange-50/60"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{issue.title}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {issue.constituency || issue.district || "Unknown area"} • {issue.status}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <ResponseModal
        isOpen={responseOpen}
        issue={selectedIssue}
        loading={responseSubmitting}
        onClose={() => {
          if (!responseSubmitting) {
            setResponseOpen(false);
            setSelectedIssue(null);
          }
        }}
        onSubmit={submitResponse}
      />
    </div>
  );
};

export default CandidateDashboard;
